import { motion } from "framer-motion";
import { useSeo } from "../lib/seo";
import { HERO_IMAGE, VENTURE_MUSIC } from "../lib/data";
import { Link } from "react-router-dom";
import CulturalContributions from "../components/CulturalContributions";

export default function About() {
  useSeo({
    title: "Shaji Nada | About — Producer & Founder of Maajja and Ideal Entertainment",
    description:
      "About Shaji Nada — Toronto-based entertainment executive. Founder of Ideal Entertainment's entertainment arm, co-founder of Maajja with A.R. Rahman since 2021.",
    canonicalPath: "/about",
  });

  return (
    <div data-testid="about-page" className="pt-32 md:pt-40 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="overline mb-6" data-testid="about-overline">About</p>
        <h1
          className="font-serif-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.98] max-w-5xl"
          data-testid="about-h1"
        >
          Shaji Nada — building the <span className="italic text-[var(--accent)]">stage</span> for a global South Asian wave.
        </h1>

        {/* 50-word press blurb */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-16 md:mt-24 grid md:grid-cols-12 gap-10"
          data-testid="about-press-blurb"
        >
          <div className="md:col-span-4">
            <p className="overline mb-4">Press blurb · 50 words</p>
          </div>
          <p className="md:col-span-8 font-serif-display text-2xl md:text-3xl text-white/85 leading-snug">
            Shaji Nada is a Toronto-based film producer and music executive. As founder of Ideal Entertainment and co-founder of Maajja with A.R. Rahman, he champions South Asian independent artists on the global stage — building the infrastructure for a new era of diaspora storytelling across music and cinema.
          </p>
        </motion.div>
      </section>

      {/* Portrait + bio */}
      <section className="mt-24 md:mt-40 max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="sticky top-32">
            <div className="aspect-[4/5] overflow-hidden bg-[#1A1A1A]">
              <img
                src={HERO_IMAGE}
                alt="Shaji Nada, Toronto-based producer and entertainment executive"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="mt-4 text-xs text-white/40 uppercase tracking-[0.22em]">Portrait · Toronto</p>
          </div>
        </div>

        <article className="md:col-span-7 prose-editorial" data-testid="about-bio">
          <p className="drop-cap">
            Shaji Nada's career has been defined by a willingness to build at the intersections — between cultures, between industries, between what a music label or a film studio is supposed to be and what they could become. From his earliest days as an entrepreneur, he was drawn to ventures that required combining instincts: operational rigor with creative patience, commercial discipline with cultural conviction, local depth with global reach.
          </p>

          <p>
            Based in Toronto, he founded the entertainment arm of Ideal Group with a clear thesis: that the next great wave of global culture would emerge from South Asian diaspora creators who refused to be categorized as regional. The bet required an executive who could move comfortably between Chennai studios and Toronto boardrooms, between traditional film financing and modern streaming economics, between the deeply personal craft of music and the rigor of building a label that could last.
          </p>

          <p>
            His partnership with A.R. Rahman, which began in 2016, has shaped much of the last decade. Producing alongside one of the most singular composers of his generation taught him to subordinate timelines to creative process — to build the kind of scaffolding that protects artistry instead of grinding against it. Their work together on 99 Songs and LeMusk explored what is possible when music is treated not as a soundtrack but as the architecture of a story. The work is patient, ambitious, and unmistakably authored.
          </p>

          <p>
            In 2021, Shaji co-founded Maajja with A.R. Rahman — an independent music label and platform dedicated to South Asian indie artists. Maajja was founded on a simple, demanding principle: artists should retain creative control and master rights. The label's early flagship, "Enjoy Enjaami," surpassed a billion streams and became a generational anthem, but the deeper achievement is infrastructural. Maajja has helped redefine what an artist-first label can look like in the streaming era, with a model that has been studied and increasingly imitated across the industry.
          </p>

          <p>
            Through Ideal Entertainment, his film and content production company, Shaji has produced a slate of culturally specific, internationally minded projects — among them She Came Back (2024), Galaxy Built on Hope (2022), and the immersive LeMusk experience. The throughline across the slate is a refusal to choose between specificity and reach: each project is rooted in a particular cultural moment and built to travel.
          </p>

          <p>
            His vision for the next decade is centered on widening the aperture for South Asian independent voices — both in music and on screen. That means more than picking great projects. It means building festivals, financing structures, distribution partnerships, and talent pipelines that allow diaspora storytelling to graduate from a "category" to a center of gravity within global culture. He talks often about the unglamorous work of infrastructure: royalty transparency, sync licensing, touring networks, and acquisition pipelines that do not require artists to surrender their identity for reach.
          </p>

          <p>
            Today, his focus is split between Maajja — which continues to expand its roster, its international partnerships, and its catalog — and Ideal Entertainment, where a new development slate is taking shape. He divides his time between Toronto and Chennai, with frequent stops in Mumbai, London, Los Angeles, and New York. He is known among collaborators for being unusually patient with creative timelines and unusually impatient with anything that wastes an artist's energy.
          </p>

          <p>
            The career arc is, in many ways, a single sentence: build the things you wished existed when you were starting out. The platforms, the partnerships, the production environments, the financing models. Build them so the next generation of artists has shorter distances to travel — and so the stories they want to tell can reach the audiences that have been waiting for them.
          </p>
        </article>
      </section>

      {/* Pull image */}
      <section className="mt-32 md:mt-48 max-w-[1600px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-center" data-testid="about-pullquote">
        <div className="md:col-span-5 order-2 md:order-1">
          <p className="overline mb-6">In his words</p>
          <blockquote className="font-serif-display text-3xl md:text-5xl text-white leading-[1.08]">
            "The job is to make space for the work to be as good as it can be — and then to get it to the audience without compromising what made it worth making in the first place."
          </blockquote>
          <Link to="/journal" data-testid="about-link-journal" className="inline-block mt-10 link-underline text-[var(--accent)] uppercase text-xs tracking-[0.22em]">
            Read the journal →
          </Link>
        </div>
        <div className="md:col-span-7 order-1 md:order-2">
          <div className="aspect-[4/3] overflow-hidden bg-[#1A1A1A]">
            <img
              src={VENTURE_MUSIC}
              alt="Shaji Nada's work in independent South Asian music"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Cultural Contributions — honoring A. R. Rahman and global artistic legacy */}
      <CulturalContributions />
    </div>
  );
}
